import { Modal, Form, Input, Select } from 'antd';
import { useTranslation } from 'react-i18next';
import { useState, useEffect } from 'react';
import type { ListOrganizationUsersDto } from '@evimo/types/api/user/list-organization-users/list-organization-users.dto';
import type { CreateOrganizationUserRequestDto } from '@evimo/types/api/user/create-organization-user/create-organization-user.request.dto';
import type { UpdateUserRequestDto } from '@evimo/types/api/user/update-user/update-user.request.dto';
import type { OrgUserRole } from '../utils';
import { useAppToast } from '~/app/hooks/useAppToast';
import { useUserManagementTableColumns } from '~/app/hooks/user/useUserManagementTableColumns';
import { EvimoTable } from '~/app/components/common/EvimoTable/EvimoTable';
import {
  FilterBarActions,
  FilterBarAddButton,
  FilterBarRefreshButton,
} from '~/app/components/common/FilterBar/FilterBarToolbar';

interface UserManagementTableProps {
  users: ListOrganizationUsersDto[];
  loading: boolean;
  role: OrgUserRole;
  onRefresh: () => void;
  onCreate: (data: CreateOrganizationUserRequestDto) => Promise<void>;
  onUpdate: (id: string, data: Partial<UpdateUserRequestDto>) => Promise<void>;
  onDelete: (id: string) => Promise<void>;
  permissions: (user: ListOrganizationUsersDto) => {
    canEdit: boolean;
    canDelete: boolean;
  };
  canCreate: boolean;
}

export function UserManagementTable({
  users,
  loading,
  role,
  onRefresh,
  onCreate,
  onUpdate,
  onDelete,
  permissions,
  canCreate,
}: UserManagementTableProps) {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingUser, setEditingUser] =
    useState<ListOrganizationUsersDto | null>(null);
  const [form] = Form.useForm();
  const { t } = useTranslation(['common', 'userManagement']);
  const { showSuccess, showError } = useAppToast();
  const { columns } = useUserManagementTableColumns({
    onDelete,
    canDelete: (record) => permissions(record).canDelete,
  });

  const roleLabel = t(`userManagement:roles.${role}`);

  useEffect(() => {
    if (editingUser) {
      form.setFieldsValue({
        name: editingUser.name,
        email: editingUser.email,
        language: editingUser.language,
        phone: editingUser.phone,
        active: editingUser.active,
      });
      setIsModalOpen(true);
    } else {
      form.resetFields();
    }
  }, [editingUser, form]);

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      if (editingUser) {
        await onUpdate(editingUser.id, values);
        showSuccess('userManagement:notifications.userUpdated');
      } else {
        await onCreate(values);
        showSuccess('userManagement:notifications.userCreated');
      }
      setIsModalOpen(false);
      setEditingUser(null);
      form.resetFields();
    } catch (error) {
      if (error instanceof Error) {
        showError('errors.unexpectedRequest', error.message);
      }
    }
  };

  return (
    <div style={{ marginTop: 24 }}>
      <div
        style={{
          marginBottom: 16,
          display: 'flex',
          justifyContent: 'flex-end',
          alignItems: 'center',
        }}
      >
        <FilterBarActions>
          <FilterBarRefreshButton onClick={onRefresh} loading={loading} />
          {canCreate ? (
            <FilterBarAddButton
              ns="userManagement"
              labelKey="modal.addButton"
              defaultLabel="Add user"
              labelValues={{ role: roleLabel }}
              onClick={() => {
                setEditingUser(null);
                setIsModalOpen(true);
              }}
            />
          ) : null}
        </FilterBarActions>
      </div>

      <EvimoTable
        dataSource={users}
        columns={columns}
        rowKey="id"
        loading={loading}
        pagination={{ pageSize: 10, showSizeChanger: true }}
        onRow={(record) => ({
          onClick: (event) => {
            const target = event.target as HTMLElement;
            if (target.closest('[data-user-row-action]')) {
              return;
            }
            const { canEdit } = permissions(record);
            if (canEdit) {
              setEditingUser(record);
            }
          },
          style: { cursor: 'pointer' },
        })}
      />

      <Modal
        title={
          editingUser
            ? t('userManagement:modal.editTitle', { role: roleLabel })
            : t('userManagement:modal.addTitle', { role: roleLabel })
        }
        open={isModalOpen}
        onOk={handleSubmit}
        onCancel={() => {
          setIsModalOpen(false);
          setEditingUser(null);
          form.resetFields();
        }}
        okText={
          editingUser
            ? t('userManagement:modal.update')
            : t('userManagement:modal.create')
        }
        destroyOnClose
      >
        <Form form={form} layout="vertical">
          <Form.Item
            name="name"
            label={t('labels.fullName')}
            rules={[
              {
                required: true,
                message: t('userManagement:validations.fullNameRequired'),
              },
            ]}
          >
            <Input placeholder={t('placeholders.fullName')} />
          </Form.Item>
          <Form.Item
            name="email"
            label={t('labels.email')}
            rules={[
              { required: true, message: t('errors.emailRequired') },
              { type: 'email', message: t('errors.emailInvalid') },
            ]}
          >
            <Input placeholder={t('placeholders.email')} />
          </Form.Item>
          {!editingUser && (
            <Form.Item
              name="password"
              label={t('labels.password')}
              rules={[
                { required: true, message: t('errors.passwordRequired') },
              ]}
            >
              <Input.Password placeholder={t('placeholders.password')} />
            </Form.Item>
          )}
          <Form.Item
            name="language"
            label={t('userManagement:labels.preferredLanguage')}
            initialValue="en"
            rules={[
              { required: true, message: t('validations.requiredField') },
            ]}
          >
            <Select placeholder={t('userManagement:placeholders.language')}>
              <Select.Option value="en">{t('languages.en')}</Select.Option>
              <Select.Option value="fr">{t('languages.fr')}</Select.Option>
              <Select.Option value="de">{t('languages.de')}</Select.Option>
            </Select>
          </Form.Item>
          <Form.Item name="phone" label={t('labels.phoneNumber')}>
            <Input placeholder={t('placeholders.phoneNumber')} />
          </Form.Item>
          {editingUser && (
            <Form.Item name="active" label={t('userManagement:table.status')}>
              <Select placeholder={t('userManagement:placeholders.status')}>
                <Select.Option value={true}>{t('status.active')}</Select.Option>
                <Select.Option value={false}>
                  {t('status.inactive')}
                </Select.Option>
              </Select>
            </Form.Item>
          )}
        </Form>
      </Modal>
    </div>
  );
}
